import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {List} from 'immutable';
import classnames from 'classnames';
import moment from 'moment-timezone';

import {resource} from '@albert-io/json-api-framework/request/builder';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';

import OnboardingBanner from './OnboardingBanner.react';
import StatusBanner from './StatusBanner/StatusBanner.react';

import './approval-statuses.scss';

export function getPersonnelRecordsQuery() {
  return resource('school_personnel_v1')
    .mandarkEndpoint([
      'teachers_v1',
      masqueradeStore.getUserIdByMasqueradeState(),
      'school_personnel_v1'
    ])
    .filter({
      any_of: [
        {
          status: 'approved'
        },
        {status: 'pending'}
      ]
    })
    .filter({
      school_v5: masqueradeStore.getSchoolId()
    })
    .include('role_v1')
    .include('school_v5');
}

export function getTeacherQuery() {
  return resource('teachers_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState()])
    .withMeta('teacher_v1')
    .customQuery({
      meta: {
        context: {
          teacher: {
            license_info: true,
            future_license_info: true
          }
        }
      }
    });
}

export function getClassroomsQuery() {
  return resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState(), 'classrooms_v1'])
    .filter({
      school_v5: masqueradeStore.getSchoolId()
    });
}

const ApprovalStatuses = ({className, theme}) => {
  const [records, setRecords] = useState(List());
  const [teacher, setTeacher] = useState(null);
  const [classrooms, setClassrooms] = useState(List());
  const [isReady, setIsReady] = useState(false);

  function fetchRecords() {
    Promise.all([
      getPersonnelRecordsQuery().getResourcePromise(),
      getTeacherQuery().getResourcePromise(),
      getClassroomsQuery().getResourcePromise()
    ])
      .then(([personnel, fetchedTeacher, classes]) => {
        const sortedRecords = personnel.sortBy((record) =>
          record.getSchool().getName().toLowerCase()
        );
        setRecords(sortedRecords);
        setTeacher(fetchedTeacher);
        setClassrooms(classes);
        setIsReady(true);
      })
      .catch(() => {
        setIsReady(false);
      });
  }

  useEffect(() => {
    fetchRecords();
  }, []);

  if (!isReady) {
    return null;
  }

  /**
   * Which personnel records should trigger a warning banner?
   * The logic here is very particular. A user can exist in
   * one of four states, per this flowchart provided by product:
   * https://drive.google.com/file/d/17Jn7M_a5qV3yDUiV_XajF1pNb8OkqFfc/view
   *
   * Briefly, the four states are "normal" (i.e., show them nothing), expiring,
   * pending, and unlicensed. It is useful to think of these as "severity" levels.
   * That is, tif a user exists in relationship to a school in any of these states,
   * the states that follow will _not_ display.  That's why we reduce over the
   * validators: in order to short circuit the operation as soon as we find positive
   * matches for each state.
   *
   * @see https://github.com/albert-io/project-management/issues/4737
   */

  function isInANormalState(record) {
    return record.getStatus() === 'approved' && !isExpiring(record) && !isUnlicensed(record);
  }

  function hasLicense(record) {
    const school = record.getSchool();
    return (
      teacher.getLicenseStatusForSchool(school.getId()) === true &&
      teacher
        .getMeta()
        .getSchoolLicensesInfo()
        .find((item) => item.get('school_id') === school.getId())
        ?.get('school_license_status') !== 'inactive'
    );
  }

  function isExpiring(record) {
    const school = record.getSchool();
    const licenseEndDate = moment(teacher.getLicenseEndDateForSchool(school.getId()));
    const now = systemTimeOffsetStore.getCurrentTime();
    const expiresSoon =
      licenseEndDate.isValid() &&
      hasLicense(record) &&
      !hasUpcomingLicense(record) &&
      moment(now).add(60, 'days').isAfter(licenseEndDate);
    return record.getStatus() === 'approved' && expiresSoon;
  }

  function isPending(record) {
    return hasLicense(record) && record.getStatus() === 'pending';
  }

  function isUnlicensed(record) {
    return !hasLicense(record) && record.getStatus() === 'approved';
  }

  const futureSchoolLicenses = teacher.getMeta().getFutureSchoolLicensesInfo();

  function hasUpcomingLicense(record) {
    const school = record.getSchool();
    const licenseEndDate = moment(teacher.getLicenseEndDateForSchool(school.getId()));
    let upcomingLicenseFound = false;
    if (futureSchoolLicenses) {
      futureSchoolLicenses.forEach((license) => {
        // Check if school even has future license
        if (license.get('school_id') === school.getId()) {
          const futureLicenseStartDate = moment(license.get('school_license_start_date'));
          // Checks if difference between current future license start date and current license end date is less than or equal to 2 days
          const isFutureLicenseUpcoming =
            futureLicenseStartDate.diff(licenseEndDate, 'minutes') < 2;
          if (isFutureLicenseUpcoming) {
            upcomingLicenseFound = true;
          }
        }
      });
    }
    return upcomingLicenseFound;
  }

  function findRecordsToRender(personnelRecords) {
    const validators = [isInANormalState, isExpiring, isPending, isUnlicensed];
    const [foundRecords] = validators.reduce(
      ([recordsToRender, allRecords], func) => {
        const matchesWereFound = !recordsToRender.isEmpty();
        if (matchesWereFound) {
          return [recordsToRender, allRecords];
        }
        return [List([...recordsToRender, ...allRecords.filter(func)]), allRecords];
      },
      [List(), personnelRecords]
    );
    return foundRecords;
  }

  const filteredRecords = findRecordsToRender(records).filterNot(isInANormalState);

  if (filteredRecords.isEmpty() && classrooms.isEmpty()) {
    return <OnboardingBanner />;
  }

  return (
    <aside className={classnames('approval-statuses', className)}>
      {filteredRecords.map((record) => (
        <StatusBanner
          className='approval-statuses__status'
          key={record.getId()}
          personnelRecord={record}
          teacher={teacher}
          theme={theme}
        />
      ))}
    </aside>
  );
};

ApprovalStatuses.propTypes = {
  className: PropTypes.string,
  theme: PropTypes.string
};

export default ApprovalStatuses;
